package diana.soleil.design.iraniancanadienbazzar.feature.onboarding

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.clickable
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BrandMark
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_add_claim_business
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_app_name
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_continue_as_user
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_create_account
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_footer
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_have_account
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_logo_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_sign_in
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_tagline
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_terms_link
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.welcome_terms_prefix
import org.jetbrains.compose.resources.stringResource

/**
 * Welcome / splash — Stitch `c321ae91044c4265b51cd958acac9a73` (Directory & Community System).
 * Tokens + string resources only. Navigation-agnostic: the host wires the callbacks.
 */
@Composable
fun WelcomeScreen(
    onContinueAsUser: () -> Unit,
    onAddOrClaimBusiness: () -> Unit,
    onSignIn: () -> Unit,
    onCreateAccount: () -> Unit = {},
    onOpenTerms: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(
                        AppTheme.colors.categoryChip,
                        AppTheme.colors.background,
                        AppTheme.colors.categoryChip,
                    ),
                ),
            )
            .safeContentPadding()
            .padding(horizontal = AppTheme.spacing.lg),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            // widthIn BEFORE fillMaxWidth — the reverse order pins the column to full width and the
            // cap is ignored, which is why the onboarding buttons stretched edge-to-edge on web.
            modifier = Modifier
                .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Logo tile — the Mahaleh mark (gold eslimi maple leaf) on brand ink, matching the
            // launcher/splash icon so onboarding and the home-screen icon read as one brand.
            Box(
                modifier = Modifier
                    .background(AppTheme.colors.brandInk, AppTheme.shapes.xxl)
                    .padding(AppTheme.spacing.xl),
            ) {
                BrandMark(
                    contentDescription = stringResource(Res.string.welcome_logo_a11y),
                    modifier = Modifier.size(AppTheme.sizes.brandMark),
                )
            }

            Spacer(Modifier.height(AppTheme.spacing.xxxl))

            // Title
            Text(
                text = stringResource(Res.string.welcome_app_name),
                style = AppTheme.typography.headlineLgMobile,
                color = AppTheme.colors.primary,
                textAlign = TextAlign.Center,
            )
            Spacer(Modifier.height(AppTheme.spacing.lg))
            // Subtitle
            Text(
                text = stringResource(Res.string.welcome_tagline),
                style = AppTheme.typography.bodyMd,
                color = AppTheme.colors.onSurfaceVariant,
                textAlign = TextAlign.Center,
            )

            Spacer(Modifier.height(AppTheme.spacing.xxxxl))

            // Primary action
            AppButton(
                text = stringResource(Res.string.welcome_continue_as_user),
                onClick = onContinueAsUser,
                modifier = Modifier.fillMaxWidth(),
                variant = AppButtonVariant.Primary,
                trailingIcon = "arrow_forward",
            )
            Spacer(Modifier.height(AppTheme.spacing.lg))
            // Create a real account (needed to claim a listing, save businesses, post).
            AppButton(
                text = stringResource(Res.string.welcome_create_account),
                onClick = onCreateAccount,
                modifier = Modifier.fillMaxWidth(),
                variant = AppButtonVariant.Secondary,
                leadingIcon = "person_add",
            )
            Spacer(Modifier.height(AppTheme.spacing.lg))
            // Secondary action
            AppButton(
                text = stringResource(Res.string.welcome_add_claim_business),
                onClick = onAddOrClaimBusiness,
                modifier = Modifier.fillMaxWidth(),
                variant = AppButtonVariant.Secondary,
                leadingIcon = "add_business",
            )

            Spacer(Modifier.height(AppTheme.spacing.xl))
            // Sign-in link
            Row(
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = stringResource(Res.string.welcome_have_account),
                    style = AppTheme.typography.labelMd,
                    color = AppTheme.colors.onSurfaceVariant,
                )
                Text(
                    text = stringResource(Res.string.welcome_sign_in),
                    style = AppTheme.typography.labelMd,
                    color = AppTheme.colors.primary,
                    modifier = Modifier.clickable(onClick = onSignIn),
                )
            }

            Spacer(Modifier.height(AppTheme.spacing.md))
            // Sign-up consent line: links the Terms/disclaimer surface (store-review requirement).
            Row(
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = stringResource(Res.string.welcome_terms_prefix),
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.onSurfaceVariant,
                )
                Text(
                    text = stringResource(Res.string.welcome_terms_link),
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.primary,
                    modifier = Modifier.clickable(onClick = onOpenTerms),
                )
            }
        }

        // Footer accent
        Column(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(bottom = AppTheme.spacing.xxl)
                .wrapContentHeight(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = stringResource(Res.string.welcome_footer).uppercase(),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant.copy(alpha = 0.5f),
                textAlign = TextAlign.Center,
            )
            Spacer(Modifier.height(AppTheme.spacing.sm))
            Box(
                modifier = Modifier
                    .width(AppTheme.spacing.xxxl)
                    .height(AppTheme.sizes.borderWidth)
                    .background(AppTheme.colors.outlineVariant),
            )
        }
    }
}

@Preview
@Composable
private fun WelcomeScreenPreview() {
    AppTheme {
        WelcomeScreen(onContinueAsUser = {}, onAddOrClaimBusiness = {}, onSignIn = {})
    }
}
